import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Archive, ArchiveRestore, ArrowLeft, ArrowRightLeft, Building2, CalendarDays, CalendarPlus, Forward, GitBranch, History, Hourglass, Loader2, MessageSquarePlus, Pencil, Phone, PhoneMissed, Play, RefreshCw, Save, Send, UserCog, UserX, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Accordion } from "@/components/ui/accordion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { StatusBadge } from "@/components/recruiting/StatusBadge";
import { PriorityBadge, StageBadge } from "@/components/recruiting/PipelineBadges";
import { ActivityTimeline } from "@/components/recruiting/ActivityTimeline";
import { ForwardLeadDialog } from "@/components/common/ForwardLeadDialog";
import { WhatsAppCallbackButton } from "@/components/common/WhatsAppCallbackButton";
import CandidateProfileFields from "@/components/candidate/CandidateProfileFields";
import { api, getErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TERMINAL_STATUSES, formatDate, formatDateTime, waLink } from "@/lib/constants";

const EVENT_LABELS = {
  CREATED: "Created",
  OWNERSHIP_CHANGE: "Ownership change",
  STAGE_CHANGE: "Stage change",
  STATUS_CHANGE: "Status change",
  PRIORITY_CHANGE: "Priority change",
  REMARK: "Remark",
  INTERVIEW_UPDATE: "Interview update",
  FORWARDED_LEAD: "Forwarded lead",
  JOINING_REFERENCE: "Joining reference",
  LOCKING_REFERENCE: "Locking reference",
  FIELD_CHANGE: "Field change",
  ARCHIVED: "Archived",
  RESTORED: "Restored",
  MIGRATED: "Migrated",
};

export default function ApplicationWorkspace() {
  const { appId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user.role === "admin" || user.role === "super_admin";
  const backPath = isAdmin ? "/admin/applications" : "/recruiter/applications";

  const [app, setApp] = useState(null);
  const [candidate, setCandidate] = useState(null);
  const [candFields, setCandFields] = useState({});
  const [masterData, setMasterData] = useState({});
  const [recruiters, setRecruiters] = useState([]);
  const [memberHistory, setMemberHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  const [statusOpen, setStatusOpen] = useState(false);
  const [targetStatus, setTargetStatus] = useState("");
  const [note, setNote] = useState("");
  const [interview, setInterview] = useState({ date: "", time: "", mode: "In-person", location_or_link: "" });
  const [busy, setBusy] = useState(false);

  const [noteText, setNoteText] = useState("");
  const [noteDisposition, setNoteDisposition] = useState("");
  const [reassignId, setReassignId] = useState("");

  // M03 pipeline state
  const [reasons, setReasons] = useState([]); // [{value, category}]
  const [pipelineOpen, setPipelineOpen] = useState(false);
  const [pipeline, setPipeline] = useState({});

  // M16: Daily Lineup panel state (direct lineup from the application)
  const [board, setBoard] = useState(null); // {today, entry}
  const [boardCompanies, setBoardCompanies] = useState([]);
  const [goingOpen, setGoingOpen] = useState(false);
  const [goingCompany, setGoingCompany] = useState("");
  const [goingProfile, setGoingProfile] = useState("");
  const [moveOpen, setMoveOpen] = useState(false);
  const [moveCompany, setMoveCompany] = useState("");
  const [moveReason, setMoveReason] = useState("");
  const [backOutOpen, setBackOutOpen] = useState(false);
  const [backOutReason, setBackOutReason] = useState("");
  const [boardBusy, setBoardBusy] = useState(false);

  // M17: recruiter 4-action flow state
  const [schedule, setSchedule] = useState(null); // active scheduled_interviews record
  const [schedOpen, setSchedOpen] = useState(false);
  const [schedForm, setSchedForm] = useState({ company_id: "", interview_date: "", role: "" });
  const [niOpen, setNiOpen] = useState(false);
  const [niNote, setNiNote] = useState("");
  const [futOpen, setFutOpen] = useState(false);
  const [futNote, setFutNote] = useState("");
  const [cbOpen, setCbOpen] = useState(false);
  const [cbForm, setCbForm] = useState({ followup_date: "", note: "" });
  const [fwdOpen, setFwdOpen] = useState(false);
  const [conflict, setConflict] = useState(null); // {message, active_application_id, candidate_name, active_app_code}

  const isOwner = app && user.role === "recruiter" && app.assigned_recruiter_id === user.id;
  const canWork = app && (isAdmin || isOwner);
  const isUnclaimed = app && !app.assigned_recruiter_id;

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/applications/${appId}`);
      setApp(res.data);
      // Recruitment Profile now reads/writes Candidate Master directly (one
      // source of truth). Every application carries a candidate_id.
      const historyUrl = res.data.member_id
        ? `/applications/member/${res.data.member_id}/history`
        : `/applications/candidate/${res.data.candidate_id}/history`;
      const [cand, md, hist] = await Promise.all([
        res.data.candidate_id ? api.get(`/candidates/${res.data.candidate_id}`) : Promise.resolve({ data: null }),
        api.get("/master-data"),
        api.get(historyUrl),
      ]);
      setCandidate(cand.data);
      setCandFields(cand.data ? { ...cand.data } : {});
      setMasterData(md.data);
      setMemberHistory(hist.data);
      api.get("/master-data/detailed", { params: { type: "application_reason" } })
        .then((r) => setReasons(r.data)).catch(() => {});
      api.get(`/lineup-board/going/by-application/${appId}`)
        .then((r) => setBoard(r.data)).catch(() => {});
      api.get("/lineup-board/options")
        .then((r) => setBoardCompanies(r.data.companies || [])).catch(() => {});
      // M17: current scheduled interview (if any) for this application
      api.get("/recruiter-flow/scheduled", { params: { application_id: appId } })
        .then((r) => {
          const items = r.data.items || [];
          setSchedule(items.find((s) => s.status === "Scheduled") || items.find((s) => s.status === "Confirmed") || null);
        }).catch(() => {});
      if (isAdmin) {
        const recs = await api.get("/users/recruiters");
        setRecruiters(recs.data.filter((r) => r.active));
      }
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [appId, isAdmin]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading || !app) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-2/3 rounded-lg" />
        <Skeleton className="h-48 rounded-xl" />
      </div>
    );
  }

  const statusOptions = (masterData.status || []).filter((s) => s !== "Application Received" && s !== app.status);
  const terminal = TERMINAL_STATUSES.includes(app.status);

  const claim = async () => {
    setBusy(true);
    try {
      const res = await api.post(`/applications/${app.id}/claim`);
      setApp(res.data);
      const extra = res.data.claimed_together || 0;
      toast.success(extra > 0
        ? `You are now working on this member — ${extra + 1} applications assigned to you`
        : "You are now working on this application");
      load();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.error_code === "ACTIVE_CANDIDATE_EXISTS") {
        setConflict(detail);
      } else {
        toast.error(getErrorMessage(err));
      }
    } finally {
      setBusy(false);
    }
  };

  // ---- M17: recruiter 4-action flow ----
  const isRecruiter = user.role === "recruiter";
  const disposed = app?.recruiter_disposition;
  const isCallback = disposed === "callback";
  const canAct = isRecruiter && isOwner && !terminal && !app?.archived && (!disposed || isCallback);

  // Phase 36: forward-lead loading/submission live in the shared ForwardLeadDialog.

  const submitSchedule = async () => {
    if (!schedForm.interview_date) return toast.error("Pick the interview date");
    if (!schedForm.company_id) return toast.error("Choose the company (your allocated companies)");
    if ((schedForm.role || "").trim().length < 2) return toast.error("Enter the role / profile");
    setBusy(true);
    try {
      const res = await api.post(`/recruiter-flow/applications/${app.id}/schedule-interview`, schedForm);
      setSchedOpen(false);
      toast.success(`Interview scheduled — ${res.data.company_name} on ${formatDate(res.data.interview_date)}. Track it in the Scheduled tab.`);
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const submitNotInterested = async () => {
    setBusy(true);
    try {
      await api.post(`/recruiter-flow/applications/${app.id}/not-interested`, { note: niNote || undefined });
      setNiOpen(false);
      toast.success("Closed as Not Interested — the candidate is available again in the Candidate Directory");
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const submitFuture = async () => {
    setBusy(true);
    try {
      await api.post(`/recruiter-flow/applications/${app.id}/future`, { note: futNote || undefined });
      setFutOpen(false);
      toast.success("Tagged as Future prospect — the candidate is parked for upcoming roles");
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const submitCallback = async () => {
    if (!cbForm.followup_date) return toast.error("Pick the follow-up (call back) date");
    setBusy(true);
    try {
      await api.post(`/recruiter-flow/applications/${app.id}/callback`, {
        followup_date: cbForm.followup_date, note: cbForm.note || undefined,
      });
      setCbOpen(false);
      toast.success(`Call back set for ${formatDate(cbForm.followup_date)} — your working slot is free. Find them under Call Backs.`);
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  // Phase 36: forward-lead submission lives in the shared ForwardLeadDialog.

  const submitStatus = async () => {
    if (!targetStatus) return toast.error("Choose the new status");
    setBusy(true);
    try {
      const payload = { status: targetStatus, note: note || undefined };
      if (targetStatus === "Interview Scheduled" && interview.date) payload.interview = interview;
      const res = await api.post(`/applications/${app.id}/status`, payload);
      setApp(res.data);
      setStatusOpen(false);
      setNote("");
      setTargetStatus("");
      toast.success(`Status updated to ${res.data.status}`);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const submitNote = async () => {
    if (!noteText.trim()) return;
    try {
      const res = await api.post(`/applications/${app.id}/notes`, { text: noteText.trim(), disposition: noteDisposition || undefined });
      setApp(res.data);
      setNoteText("");
      setNoteDisposition("");
      toast.success("Note added");
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const submitReassign = async () => {
    if (!reassignId) return toast.error("Choose a recruiter");
    setBusy(true);
    try {
      const res = await api.post(`/applications/${app.id}/reassign`, { recruiter_id: reassignId });
      setApp(res.data);
      toast.success(`Assigned to ${res.data.recruiter?.name}`);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const openPipeline = () => {
    setPipeline({
      stage: app.stage || "",
      business_status: app.business_status || "",
      priority: app.priority || "",
      reason: app.reason || "",
      remarks: app.remarks || "",
      expected_interview_date: app.expected_interview_date || "",
      expected_joining_date: app.expected_joining_date || "",
    });
    setPipelineOpen(true);
  };

  const submitPipeline = async () => {
    setBusy(true);
    try {
      const payload = {};
      for (const [k, v] of Object.entries(pipeline)) {
        if ((v || "") !== (app[k] || "")) payload[k] = v || "";
      }
      if (Object.keys(payload).length === 0) {
        setPipelineOpen(false);
        return;
      }
      const res = await api.put(`/applications/${app.id}/pipeline`, payload);
      setApp(res.data);
      setPipelineOpen(false);
      toast.success("Pipeline updated");
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const toggleArchive = async () => {
    setBusy(true);
    try {
      const res = await api.post(`/applications/${app.id}/${app.archived ? "restore" : "archive"}`);
      setApp(res.data);
      toast.success(res.data.archived ? "Application archived" : "Application restored");
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const saveProfile = async () => {
    if (!candidate) return toast.error("No candidate linked to this application");
    setBusy(true);
    try {
      const res = await api.put(`/candidates/${candidate.id}`, { fields: candFields });
      setCandidate(res.data);
      setCandFields({ ...res.data });
      toast.success("Candidate profile saved");
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const history = [...(app.status_history || [])].reverse();

  // ---- M16: Daily Lineup actions (direct from the application) ----
  const entry = board?.entry || null;
  const activeGoing = entry && entry.board_status === "Going";
  const refreshBoard = () =>
    api.get(`/lineup-board/going/by-application/${app.id}`).then((r) => setBoard(r.data)).catch(() => {});

  const MAIL_BADGE = {
    "Pending": "bg-slate-100 text-slate-700",
    "Queued - No Recipient": "bg-amber-100 text-amber-800",
    "Sent": "bg-emerald-100 text-emerald-800",
    "Failed": "bg-red-100 text-red-700",
  };

  const openGoing = () => {
    setGoingCompany("");
    setGoingProfile(app.job?.title || app.job_title || "");
    setGoingOpen(true);
  };

  const submitGoing = async () => {
    if (!goingCompany) return toast.error("Choose the company this candidate is going to");
    setBoardBusy(true);
    try {
      await api.post("/lineup-board/going", {
        application_id: app.id,
        company_id: goingCompany,
        job_title: goingProfile.trim() || undefined,
      });
      const cname = boardCompanies.find((c) => c.id === goingCompany)?.name || "the company";
      toast.success(`Marked Going for ${cname} — added to today's Daily Lineup`);
      setGoingOpen(false);
      refreshBoard();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBoardBusy(false);
    }
  };

  const submitMove = async () => {
    if (!moveCompany) return toast.error("Choose the new company");
    if (moveReason.trim().length < 3) return toast.error("Add a short reason for the change");
    setBoardBusy(true);
    try {
      await api.put(`/lineup-board/${entry.id}/company`, { company_id: moveCompany, reason: moveReason.trim() });
      const cname = boardCompanies.find((c) => c.id === moveCompany)?.name || "the new company";
      toast.success(`Company changed to ${cname} — goes out in the next mail batch`);
      setMoveOpen(false);
      setMoveCompany("");
      setMoveReason("");
      refreshBoard();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBoardBusy(false);
    }
  };

  const submitBackOut = async () => {
    if (backOutReason.trim().length < 3) return toast.error("Add a short reason");
    setBoardBusy(true);
    try {
      await api.post(`/lineup-board/${entry.id}/back-out`, { reason: backOutReason.trim() });
      toast.success("Backed out — application closed. To re-engage this candidate, reassign from the Candidate Directory.");
      setBackOutOpen(false);
      setBackOutReason("");
      refreshBoard();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBoardBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <Link to={backPath} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="workspace-back-link">
        <ArrowLeft className="h-4 w-4" /> Applications
      </Link>

      {/* Header: Candidate Name, Mobile, Applied Job, Current Status */}
      <div className="flex flex-col lg:flex-row lg:items-center gap-4">
        <span className="h-14 w-14 rounded-2xl bg-primary/10 text-primary flex items-center justify-center font-heading font-bold text-xl shrink-0">
          {(app.member?.name || app.candidate?.full_name || "?").charAt(0).toUpperCase()}
        </span>
        <div className="flex-1 min-w-0">
          <h1 className="text-xl sm:text-2xl font-heading font-semibold tracking-tight" data-testid="workspace-candidate-name">{app.member?.name || app.candidate?.full_name}</h1>
          <p className="text-sm text-muted-foreground mt-0.5 flex items-center gap-3 flex-wrap">
            <a href={`tel:${app.member?.mobile || app.candidate?.mobile}`} className="flex items-center gap-1 hover:text-foreground"><Phone className="h-3.5 w-3.5" /><span className="font-mono">{app.member?.mobile || app.candidate?.mobile}</span></a>
            <a href={waLink(app.member?.mobile || app.candidate?.mobile)} target="_blank" rel="noreferrer" className="text-emerald-700 hover:underline">WhatsApp</a>
            <span className="flex items-center gap-1"><Building2 className="h-3.5 w-3.5" />{app.job?.hiring_brand} — <span className="font-medium text-foreground">{app.job?.title}</span></span>
            <span className="font-mono text-xs" data-testid="workspace-app-code">{app.app_code || app.code}</span>
            {app.candidate?.code && <Link to={isAdmin ? `/admin/candidates/${app.candidate.id}` : `/recruiter/candidates/${app.candidate.id}`} className="font-mono text-xs text-primary hover:underline" data-testid="workspace-candidate-code">{app.candidate.code}</Link>}
            {app.archived && <span className="inline-flex rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 text-[11px] font-medium" data-testid="workspace-archived-badge">Archived</span>}
          </p>
        </div>
        <div className="flex items-center gap-3 shrink-0 flex-wrap">
          <StageBadge stage={app.stage} className="text-sm px-3 py-1" />
          <StatusBadge status={app.status} className="text-sm px-3 py-1" />
          {isUnclaimed && user.role === "recruiter" && (
            <Button onClick={claim} disabled={busy} className="rounded-lg" data-testid="workspace-start-working-button">
              <Play className="h-4 w-4 mr-2" /> Start Working
            </Button>
          )}
          {canWork && !(terminal && !isAdmin) && !app.archived && isAdmin && (
            <Button onClick={() => setStatusOpen(true)} className="rounded-lg" data-testid="workspace-update-status-button">
              <RefreshCw className="h-4 w-4 mr-2" /> Update Status
            </Button>
          )}
          {isAdmin && (
            <Button onClick={toggleArchive} disabled={busy} variant="outline" className="rounded-lg bg-white" data-testid="workspace-archive-button">
              {app.archived ? <><ArchiveRestore className="h-4 w-4 mr-2" /> Restore</> : <><Archive className="h-4 w-4 mr-2" /> Archive</>}
            </Button>
          )}
        </div>
      </div>

      {/* Admin reassignment */}
      {isAdmin && (
        <Card className="rounded-xl border-border shadow-none">
          <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex items-center gap-2 text-sm font-medium">
              <UserCog className="h-4 w-4 text-primary" />
              {app.recruiter ? <span>Owner: <span className="font-semibold">{app.recruiter.name}</span></span> : <span>Unassigned</span>}
            </div>
            <div className="flex items-center gap-2 sm:ml-auto">
              <Select value={reassignId} onValueChange={setReassignId}>
                <SelectTrigger className="w-56 bg-white" data-testid="reassign-recruiter-select">
                  <SelectValue placeholder="Choose recruiter..." />
                </SelectTrigger>
                <SelectContent>
                  {recruiters.map((r) => <SelectItem key={r.id} value={r.id}>{r.name} ({r.active_assignments} active)</SelectItem>)}
                </SelectContent>
              </Select>
              <Button onClick={submitReassign} disabled={busy} variant="outline" className="rounded-lg bg-white" data-testid="reassign-recruiter-button">
                {app.recruiter ? "Reassign" : "Assign"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid lg:grid-cols-3 gap-6 items-start">
        <div className="lg:col-span-2 min-w-0">
          <Tabs defaultValue="info">
            <TabsList className="max-w-full overflow-x-auto justify-start">
              <TabsTrigger value="info" data-testid="workspace-tab-info">Candidate Information</TabsTrigger>
              <TabsTrigger value="notes" data-testid="workspace-tab-notes">Recruiter Notes ({app.notes?.length || 0})</TabsTrigger>
              <TabsTrigger value="history" data-testid="workspace-tab-history">History ({(app.history || []).length})</TabsTrigger>
            </TabsList>

            <TabsContent value="info" className="mt-4">
              <Card className="rounded-xl border-border shadow-none">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-heading">Recruitment profile</CardTitle>
                  <p className="text-xs text-muted-foreground">Full live Candidate Master profile — edits here reflect instantly in Candidate Directory.</p>
                </CardHeader>
                <CardContent className="space-y-4">
                  {!candidate ? (
                    <p className="text-sm text-muted-foreground">No candidate record linked to this application.</p>
                  ) : (
                    <div data-testid="candidate-master-fields">
                      <Accordion type="multiple" defaultValue={["personal", "professional"]} className="w-full">
                        <CandidateProfileFields
                          cand={candidate}
                          form={candFields}
                          set={(k, v) => setCandFields((f) => ({ ...f, [k]: v }))}
                          master={masterData}
                          canEdit={canWork}
                        />
                      </Accordion>
                    </div>
                  )}
                  {canWork ? (
                    <Button onClick={saveProfile} disabled={busy} className="rounded-lg" data-testid="save-candidate-info-button">
                      <Save className="h-4 w-4 mr-2" /> Save Candidate Information
                    </Button>
                  ) : (
                    <p className="text-xs text-muted-foreground">Start working on this application to edit candidate information.</p>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="notes" className="mt-4 space-y-4">
              {canWork && (
                <Card className="rounded-xl border-border shadow-none">
                  <CardContent className="p-4 space-y-3">
                    <Textarea data-testid="note-text-input" rows={3} placeholder="Call summary, candidate feedback, next steps..." value={noteText} onChange={(e) => setNoteText(e.target.value)} />
                    <div className="flex flex-col sm:flex-row gap-2.5">
                      <Select value={noteDisposition} onValueChange={setNoteDisposition}>
                        <SelectTrigger className="sm:w-56" data-testid="note-disposition-select"><SelectValue placeholder="Disposition (optional)" /></SelectTrigger>
                        <SelectContent>
                          {(masterData.disposition || []).map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <Button onClick={submitNote} size="sm" className="rounded-lg sm:ml-auto" data-testid="add-note-button">
                        <MessageSquarePlus className="h-4 w-4 mr-2" /> Add note
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}
              {(app.notes || []).slice().reverse().map((n) => (
                <Card key={n.id} className="rounded-xl border-border shadow-none">
                  <CardContent className="p-4">
                    <p className="text-sm whitespace-pre-line">{n.text}</p>
                    <p className="text-[11px] text-muted-foreground mt-2">
                      {n.disposition && <span className="inline-flex rounded-full bg-muted px-2 py-0.5 mr-2 text-[10px] font-medium">{n.disposition}</span>}
                      {n.author_name} · <span className="font-mono">{formatDateTime(n.created_at)}</span>
                    </p>
                  </CardContent>
                </Card>
              ))}
              {(app.notes || []).length === 0 && <p className="text-sm text-muted-foreground px-1">No notes yet.</p>}
            </TabsContent>

            <TabsContent value="history" className="mt-4">
              <Card className="rounded-xl border-border shadow-none">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-heading flex items-center gap-2">
                    <History className="h-4 w-4 text-primary" /> Application history
                  </CardTitle>
                  <p className="text-xs text-muted-foreground">Every change is recorded permanently — who, when, old and new value. History is never edited.</p>
                </CardHeader>
                <CardContent className="space-y-2.5" data-testid="application-event-history">
                  {[...(app.history || [])].reverse().map((e) => (
                    <div key={e.id} className="rounded-lg border border-border p-3" data-testid="history-event-item">
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <span className="inline-flex rounded-full bg-primary/10 text-primary px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide">
                          {EVENT_LABELS[e.event_type] || e.event_type}
                        </span>
                        <span className="text-[11px] text-muted-foreground font-mono">{formatDateTime(e.at)}</span>
                      </div>
                      {(e.old !== null || e.new !== null) && (e.old !== undefined || e.new !== undefined) && (
                        <p className="text-sm mt-1.5">
                          {e.field && <span className="text-muted-foreground">{e.field.replaceAll("_", " ")}: </span>}
                          {typeof e.old === "object" && e.old !== null ? JSON.stringify(e.old) : (e.old ?? "—")}
                          <span className="text-muted-foreground mx-1.5">→</span>
                          <span className="font-medium">{typeof e.new === "object" && e.new !== null ? JSON.stringify(e.new) : (e.new ?? "—")}</span>
                        </p>
                      )}
                      {e.remarks && <p className="text-xs text-muted-foreground mt-1 whitespace-pre-line">{e.remarks}</p>}
                      <p className="text-[11px] text-muted-foreground mt-1.5">by <span className="font-medium text-foreground">{e.actor_name}</span>{e.actor_role && e.actor_role !== "system" ? ` (${e.actor_role.replaceAll("_", " ")})` : ""}</p>
                    </div>
                  ))}
                  {(app.history || []).length === 0 && <p className="text-sm text-muted-foreground">No history recorded yet.</p>}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>

        <div className="space-y-6 min-w-0">
          {/* M17: recruiter next-step actions — exactly four, all free the slot */}
          {isRecruiter && isOwner && !app.archived && (
            <Card className="rounded-xl border-primary/30 shadow-none" data-testid="recruiter-actions-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-heading flex items-center gap-2">
                  <Play className="h-4 w-4 text-primary" /> Next step for this candidate
                </CardTitle>
                {canAct && (
                  <p className="text-xs text-muted-foreground">
                    Choose one action. Each one completes your work on this candidate and lets you pick the next one.
                  </p>
                )}
              </CardHeader>
              <CardContent className="space-y-2.5 text-sm">
                {canAct ? (
                  <>
                    {isCallback && (
                      <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 space-y-0.5" data-testid="callback-banner">
                        <p className="font-medium text-amber-900 flex items-center gap-1.5">
                          <PhoneMissed className="h-4 w-4" /> Awaiting call back on {formatDate(app.callback_date)}
                          {app.callback_count > 1 && (
                            <span className="ml-1 text-xs font-normal text-amber-700">· attempt #{app.callback_count}</span>
                          )}
                        </p>
                        {app.callback_note && <p className="text-xs text-amber-800">{app.callback_note}</p>}
                        <p className="text-[11px] text-amber-700">Ownership is held with you. Reschedule below, or pick a final action once you connect.</p>
                      </div>
                    )}
                    <Button className="w-full rounded-lg justify-start gap-2" onClick={() => {
                      setSchedForm({ company_id: "", interview_date: "", role: app.job?.title || "" });
                      setSchedOpen(true);
                    }} data-testid="action-schedule-btn">
                      <CalendarPlus className="h-4 w-4" /> Schedule Interview
                    </Button>
                    <Button variant="outline" className="w-full rounded-lg justify-start gap-2 bg-white" onClick={() => {
                      setFwdOpen(true);
                    }} data-testid="action-forward-btn">
                      <Forward className="h-4 w-4 text-sky-600" /> Forward Lead
                    </Button>
                    <Button variant="outline" className="w-full rounded-lg justify-start gap-2 bg-white" onClick={() => {
                      setFutNote(""); setFutOpen(true);
                    }} data-testid="action-future-btn">
                      <Hourglass className="h-4 w-4 text-amber-600" /> Future
                    </Button>
                    <Button variant="outline" className="w-full rounded-lg justify-start gap-2 bg-white text-red-600 hover:text-red-700" onClick={() => {
                      setNiNote(""); setNiOpen(true);
                    }} data-testid="action-not-interested-btn">
                      <UserX className="h-4 w-4" /> Not Interested
                    </Button>
                    <Button variant="outline" className="w-full rounded-lg justify-start gap-2 bg-white text-amber-700 hover:text-amber-800" onClick={() => {
                      setCbForm({ followup_date: app.callback_date || "", note: app.callback_note || "" }); setCbOpen(true);
                    }} data-testid="action-callback-btn">
                      <PhoneMissed className="h-4 w-4" /> {isCallback ? "Reschedule Call Back" : "No Answer / Call Back"}
                    </Button>
                    <WhatsAppCallbackButton
                      candidateName={app.member?.name || app.candidate?.full_name || app.name}
                      candidateMobile={app.member?.mobile || app.candidate?.mobile}
                      className="w-full justify-start" />
                  </>
                ) : disposed ? (
                  <div className="rounded-lg border border-border bg-muted/40 p-3 space-y-1.5" data-testid="disposition-card">
                    <p className="font-medium">
                      {disposed === "scheduled" && "Interview scheduled"}
                      {disposed === "forwarded" && "Forwarded as a lead"}
                      {disposed === "not_interested" && "Closed — candidate not interested"}
                      {disposed === "future" && "Parked as a Future prospect"}
                    </p>
                    {disposed === "scheduled" && schedule && (
                      <p className="text-xs text-muted-foreground">
                        {schedule.company_name} · {formatDate(schedule.interview_date)} · {schedule.role}
                        {schedule.status === "Confirmed" && " — confirmed Going"}
                      </p>
                    )}
                    {disposed === "scheduled" && (
                      <Link to="/recruiter/scheduled" className="text-xs text-primary hover:underline inline-block" data-testid="disposition-scheduled-link">
                        Open the Scheduled tab
                      </Link>
                    )}
                    {disposed === "forwarded" && (
                      <Link to="/recruiter/forward-leads" className="text-xs text-primary hover:underline inline-block" data-testid="disposition-forward-link">
                        Track it under Forward Leads
                      </Link>
                    )}
                    <p className="text-[11px] text-muted-foreground">Your working slot is free — you can pick your next candidate.</p>
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground">This application is closed ('{app.status}').</p>
                )}
              </CardContent>
            </Card>
          )}

          <Card className="rounded-xl border-primary/20 shadow-none" data-testid="pipeline-card">
            <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
              <CardTitle className="text-sm font-heading flex items-center gap-2">
                <GitBranch className="h-4 w-4 text-primary" /> Recruitment pipeline
              </CardTitle>
              {isAdmin && canWork && !app.archived && (
                <Button variant="outline" size="sm" className="h-7 rounded-lg bg-white" onClick={openPipeline} data-testid="pipeline-edit-button">
                  <Pencil className="h-3 w-3 mr-1.5" /> Update
                </Button>
              )}
            </CardHeader>
            <CardContent className="text-sm space-y-2">
              <div className="flex items-center justify-between gap-2">
                <span className="text-muted-foreground">Stage</span>
                <StageBadge stage={app.stage} className="text-[11px]" />
              </div>
              <div className="flex items-center justify-between gap-2">
                <span className="text-muted-foreground">Status</span>
                <span className="font-medium" data-testid="pipeline-business-status">{app.business_status || "—"}</span>
              </div>
              {app.joining_register_id && (
                <div className="flex items-center justify-between gap-2">
                  <span className="text-muted-foreground">Employment</span>
                  <Link to={`${isAdmin ? "/admin" : "/recruiter"}/employment/${app.joining_register_id}`}
                    className="font-mono text-xs text-primary hover:underline" data-testid="pipeline-employment-link">
                    View record
                  </Link>
                </div>
              )}
              <div className="flex items-center justify-between gap-2">
                <span className="text-muted-foreground">Priority</span>
                <PriorityBadge priority={app.priority} className="text-[11px]" />
              </div>
              {app.reason && (
                <div className="flex items-center justify-between gap-2">
                  <span className="text-muted-foreground">Reason</span>
                  <span className="font-medium text-right" data-testid="pipeline-reason">{app.reason}</span>
                </div>
              )}
              <div className="flex items-center justify-between gap-2">
                <span className="text-muted-foreground">Expected interview</span>
                <span className="font-medium">{formatDate(app.expected_interview_date)}</span>
              </div>
              <div className="flex items-center justify-between gap-2">
                <span className="text-muted-foreground">Expected joining</span>
                <span className="font-medium">{formatDate(app.expected_joining_date)}</span>
              </div>
              {app.remarks && (
                <p className="text-xs text-muted-foreground border-t border-border pt-2 whitespace-pre-line" data-testid="pipeline-remarks">{app.remarks}</p>
              )}
            </CardContent>
          </Card>

          {/* M16: Daily Lineup — pick/change company + Going, directly here */}
          {(isAdmin || entry) && (
          <Card className="rounded-xl border-emerald-200/70 shadow-none" data-testid="daily-lineup-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-heading flex items-center gap-2">
                <Send className="h-4 w-4 text-emerald-600" /> Daily Lineup — today
              </CardTitle>
              <p className="text-xs text-muted-foreground">
                Pick the company this candidate is going to. The lineup email is grouped and sent
                automatically from the Daily Lineup board{!isAdmin && " (your allocated companies only)"}.
              </p>
            </CardHeader>
            <CardContent className="text-sm space-y-3">
              {activeGoing ? (
                <>
                  <div className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-3 space-y-1.5" data-testid="lineup-going-status">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-semibold">{entry.company_name}</span>
                      <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold ${MAIL_BADGE[entry.mail_status] || "bg-slate-100 text-slate-700"}`} data-testid="lineup-mail-status">
                        {entry.mail_status === "Queued - No Recipient" ? "Queued — no recipient" : entry.mail_status}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Going today · <span className="font-mono">{entry.going_code}</span>
                      {entry.job_title && <> · {entry.job_title}</>}
                    </p>
                    {(entry.previous_companies || []).length > 0 && (
                      <p className="text-[11px] text-amber-700" data-testid="lineup-prev-companies">
                        Changed {entry.previous_companies.length}× today (was {entry.previous_companies[entry.previous_companies.length - 1].company_name})
                      </p>
                    )}
                  </div>
                  {canWork && !app.archived && (
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" className="rounded-lg flex-1 gap-1.5 bg-white"
                              onClick={() => { setMoveCompany(""); setMoveReason(""); setMoveOpen(true); }}
                              data-testid="lineup-change-company-btn">
                        <ArrowRightLeft className="h-3.5 w-3.5" /> Change company
                      </Button>
                      <Button variant="outline" size="sm" className="rounded-lg gap-1.5 bg-white text-red-600 hover:text-red-700"
                              onClick={() => setBackOutOpen(true)} data-testid="lineup-backout-btn">
                        <XCircle className="h-3.5 w-3.5" /> Back out
                      </Button>
                    </div>
                  )}
                </>
              ) : (
                <>
                  {entry && entry.board_status === "Backed Out" && (
                    <p className="text-xs rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-amber-800" data-testid="lineup-backed-out-note">
                      Backed out of {entry.company_name} earlier today — you can line them up again.
                    </p>
                  )}
                  {entry && entry.board_status === "Interview Happened" && (
                    <p className="text-xs rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-emerald-800" data-testid="lineup-interview-happened-note">
                      Interview at {entry.company_name} completed — you can line the candidate up for another company today.
                    </p>
                  )}
                  {!entry && <p className="text-muted-foreground text-xs" data-testid="lineup-none-note">Not lined up for any company today.</p>}
                  {canWork && !app.archived && isAdmin ? (
                    <Button size="sm" className="rounded-lg w-full gap-1.5" onClick={openGoing} data-testid="lineup-mark-going-btn">
                      <Send className="h-3.5 w-3.5" /> Line up & mark Going
                    </Button>
                  ) : !isAdmin ? (
                    <p className="text-xs text-muted-foreground">Use <span className="font-medium text-foreground">Schedule Interview</span> below — confirming it from the Scheduled tab puts the candidate on the board.</p>
                  ) : (
                    <p className="text-xs text-muted-foreground">Start working on this application to line up this candidate.</p>
                  )}
                </>
              )}
            </CardContent>
          </Card>
          )}

          <Card className="rounded-xl border-border shadow-none">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-heading">Application details</CardTitle>
            </CardHeader>
            <CardContent className="text-sm space-y-1.5">
              <p><span className="text-muted-foreground">Application ID:</span> <span className="font-mono font-medium">{app.app_code || app.code}</span></p>
              {app.app_code && app.code && <p><span className="text-muted-foreground">Legacy ID:</span> <span className="font-mono">{app.code}</span></p>}
              <p><span className="text-muted-foreground">Applied:</span> <span className="font-medium">{formatDate(app.created_at)}</span></p>
              <p><span className="text-muted-foreground">Source:</span> <span className="font-medium">{app.source}</span></p>
              {app.last_disposition && <p><span className="text-muted-foreground">Last disposition:</span> <span className="font-medium">{app.last_disposition}</span></p>}
            </CardContent>
          </Card>

          {app.interview && (
            <Card className="rounded-xl border-violet-200 bg-violet-50/50 shadow-none" data-testid="workspace-interview-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-heading flex items-center gap-2">
                  <CalendarDays className="h-4 w-4 text-violet-600" /> Interview
                </CardTitle>
              </CardHeader>
              <CardContent className="text-sm space-y-1.5">
                <p><span className="text-muted-foreground">Date:</span> <span className="font-medium">{formatDate(app.interview.date)}</span></p>
                {app.interview.time && <p><span className="text-muted-foreground">Time:</span> <span className="font-medium">{app.interview.time}</span></p>}
                {app.interview.mode && <p><span className="text-muted-foreground">Mode:</span> <span className="font-medium">{app.interview.mode}</span></p>}
                {app.interview.location_or_link && <p className="break-all"><span className="text-muted-foreground">Where:</span> <span className="font-medium">{app.interview.location_or_link}</span></p>}
              </CardContent>
            </Card>
          )}

          <Card className="rounded-xl border-border shadow-none" data-testid="member-history-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-heading flex items-center gap-2">
                <History className="h-4 w-4 text-primary" /> Application history ({memberHistory.length})
              </CardTitle>
              <p className="text-xs text-muted-foreground">Every job this candidate applied to, and who is working on it.</p>
            </CardHeader>
            <CardContent className="space-y-2.5">
              {memberHistory.map((h) => {
                const isCurrent = h.id === app.id;
                const clickable = !isCurrent && (isAdmin || !h.assigned_recruiter_id || h.assigned_recruiter_id === user.id);
                const inner = (
                  <div
                    className={`rounded-lg border p-3 ${isCurrent ? "border-primary/40 bg-primary/5" : "border-border"} ${clickable ? "hover:border-primary/40 hover:bg-muted/50 transition-colors" : ""}`}
                    data-testid="member-history-item"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-sm font-medium leading-tight truncate">{h.job_title}</p>
                        <p className="text-[11px] text-muted-foreground mt-0.5">{h.hiring_brand} · <span className="font-mono">{h.code}</span> · {formatDate(h.applied_at)}</p>
                      </div>
                      <StatusBadge status={h.status} className="text-[10px] px-2 py-0.5 shrink-0" />
                    </div>
                    <p className="text-[11px] mt-1.5">
                      {h.recruiter_name
                        ? <span className="text-muted-foreground">Recruiter: <span className="font-medium text-foreground">{h.recruiter_name}</span>{h.assigned_recruiter_id === user.id && " (you)"}</span>
                        : <span className="text-amber-700 font-medium">Unassigned — no one is working on this yet</span>}
                      {isCurrent && <span className="ml-2 inline-flex rounded-full bg-primary/10 text-primary px-2 py-0.5 text-[10px] font-medium">Currently viewing</span>}
                    </p>
                  </div>
                );
                return clickable
                  ? <Link key={h.id} to={`${backPath}/${h.id}`} className="block">{inner}</Link>
                  : <div key={h.id}>{inner}</div>;
              })}
              {memberHistory.length === 0 && <p className="text-sm text-muted-foreground">No applications found.</p>}
            </CardContent>
          </Card>

          <Card className="rounded-xl border-border shadow-none">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-heading">Status history</CardTitle>
            </CardHeader>
            <CardContent>
              <ActivityTimeline items={history} />
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Status update dialog */}
      <Dialog open={statusOpen} onOpenChange={setStatusOpen}>
        <DialogContent className="sm:max-w-md rounded-xl" data-testid="status-update-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Update status</DialogTitle>
            <DialogDescription>Current: <span className="font-medium text-foreground">{app.status}</span></DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>New status</Label>
              <Select value={targetStatus} onValueChange={setTargetStatus}>
                <SelectTrigger data-testid="status-select"><SelectValue placeholder="Choose status..." /></SelectTrigger>
                <SelectContent>
                  {statusOptions.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {targetStatus === "Interview Scheduled" && (
              <div className="grid grid-cols-2 gap-3" data-testid="interview-fields">
                <div className="space-y-1.5">
                  <Label>Date</Label>
                  <Input type="date" data-testid="interview-date-input" value={interview.date} onChange={(e) => setInterview({ ...interview, date: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label>Time</Label>
                  <Input type="time" data-testid="interview-time-input" value={interview.time} onChange={(e) => setInterview({ ...interview, time: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label>Mode</Label>
                  <Select value={interview.mode} onValueChange={(v) => setInterview({ ...interview, mode: v })}>
                    <SelectTrigger data-testid="interview-mode-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="In-person">In-person</SelectItem>
                      <SelectItem value="Video Call">Video Call</SelectItem>
                      <SelectItem value="Phone">Phone</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Location / Link</Label>
                  <Input data-testid="interview-location-input" placeholder="Address or meeting link" value={interview.location_or_link} onChange={(e) => setInterview({ ...interview, location_or_link: e.target.value })} />
                </div>
              </div>
            )}

            <div className="space-y-1.5">
              <Label>Note (optional)</Label>
              <Textarea data-testid="status-note-input" rows={2} placeholder="Add context for this change..." value={note} onChange={(e) => setNote(e.target.value)} />
            </div>

            <Button onClick={submitStatus} disabled={busy} className="w-full rounded-lg" data-testid="status-confirm-button">
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Update to {targetStatus || "..."}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      {/* M03: pipeline update dialog — all values come from Master Data */}
      <Dialog open={pipelineOpen} onOpenChange={setPipelineOpen}>
        <DialogContent className="sm:max-w-md rounded-xl" data-testid="pipeline-update-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Update pipeline</DialogTitle>
            <DialogDescription>Every change is recorded permanently in the application history.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Stage</Label>
                <Select value={pipeline.stage || ""} onValueChange={(v) => setPipeline({ ...pipeline, stage: v })}>
                  <SelectTrigger data-testid="pipeline-stage-select"><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>
                    {(masterData.application_stage || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Status</Label>
                <Select value={pipeline.business_status || ""} onValueChange={(v) => setPipeline({ ...pipeline, business_status: v })}>
                  <SelectTrigger data-testid="pipeline-status-select"><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>
                    {(masterData.application_status || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Priority</Label>
                <Select value={pipeline.priority || ""} onValueChange={(v) => setPipeline({ ...pipeline, priority: v })}>
                  <SelectTrigger data-testid="pipeline-priority-select"><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>
                    {(masterData.application_priority || []).map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Reason</Label>
                <Select value={pipeline.reason || ""} onValueChange={(v) => setPipeline({ ...pipeline, reason: v })}>
                  <SelectTrigger data-testid="pipeline-reason-select"><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>
                    {[...new Set(reasons.map((r) => r.category || "Other"))].map((cat) => (
                      <SelectGroup key={cat}>
                        <SelectLabel>{cat}</SelectLabel>
                        {reasons.filter((r) => (r.category || "Other") === cat).map((r) => (
                          <SelectItem key={r.value} value={r.value}>{r.value}</SelectItem>
                        ))}
                      </SelectGroup>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Expected interview</Label>
                <Input type="date" data-testid="pipeline-interview-date-input" value={pipeline.expected_interview_date || ""} onChange={(e) => setPipeline({ ...pipeline, expected_interview_date: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label>Expected joining</Label>
                <Input type="date" data-testid="pipeline-joining-date-input" value={pipeline.expected_joining_date || ""} onChange={(e) => setPipeline({ ...pipeline, expected_joining_date: e.target.value })} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Remarks</Label>
              <Textarea data-testid="pipeline-remarks-input" rows={2} placeholder="Context for this update..." value={pipeline.remarks || ""} onChange={(e) => setPipeline({ ...pipeline, remarks: e.target.value })} />
            </div>
            <Button onClick={submitPipeline} disabled={busy} className="w-full rounded-lg" data-testid="pipeline-confirm-button">
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Save pipeline changes
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      {/* M16: mark Going dialog */}
      <Dialog open={goingOpen} onOpenChange={setGoingOpen}>
        <DialogContent className="sm:max-w-md rounded-xl" data-testid="lineup-going-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Line up & mark Going — today</DialogTitle>
            <DialogDescription>
              {app.member?.name || app.candidate?.full_name} will appear on today's Daily Lineup and the
              company email goes out automatically{!isAdmin && " (you can pick from your allocated companies)"}.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Company<span className="text-destructive ml-0.5">*</span></Label>
              <Select value={goingCompany} onValueChange={setGoingCompany}>
                <SelectTrigger data-testid="going-company-select"><SelectValue placeholder="Choose company..." /></SelectTrigger>
                <SelectContent>
                  {boardCompanies.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}{!c.has_recipients ? " (no email recipient set)" : ""}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {boardCompanies.length === 0 && (
                <p className="text-xs text-amber-700">No companies allocated to you yet — ask your admin for allocation.</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label>Profile / role shown in the email</Label>
              <Input data-testid="going-profile-input" value={goingProfile}
                     onChange={(e) => setGoingProfile(e.target.value)} placeholder="e.g. Telecaller - Hindi" />
              <p className="text-[11px] text-muted-foreground">Pre-filled from the application — edit if this company needs a different role.</p>
            </div>
            <Button onClick={submitGoing} disabled={boardBusy} className="w-full rounded-lg" data-testid="going-confirm-btn">
              {boardBusy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Mark Going
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* M16: change company dialog */}
      <Dialog open={moveOpen} onOpenChange={setMoveOpen}>
        <DialogContent className="sm:max-w-md rounded-xl" data-testid="lineup-move-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Change company — same day</DialogTitle>
            <DialogDescription>
              Currently going to <span className="font-medium text-foreground">{entry?.company_name}</span>.
              The new company's email goes out in the next 15-minute batch; full history is kept.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>New company<span className="text-destructive ml-0.5">*</span></Label>
              <Select value={moveCompany} onValueChange={setMoveCompany}>
                <SelectTrigger data-testid="move-company-select"><SelectValue placeholder="Choose company..." /></SelectTrigger>
                <SelectContent>
                  {boardCompanies.filter((c) => c.id !== entry?.company_id).map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}{!c.has_recipients ? " (no email recipient set)" : ""}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {boardCompanies.filter((c) => c.id !== entry?.company_id).length === 0 && (
                <p className="text-xs text-amber-700" data-testid="move-no-companies-note">
                  No other {!isAdmin ? "allocated " : ""}companies available{!isAdmin ? " — ask your admin to allocate another company" : ""}.
                </p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label>Reason<span className="text-destructive ml-0.5">*</span></Label>
              <Textarea rows={2} data-testid="move-reason-input" value={moveReason}
                        onChange={(e) => setMoveReason(e.target.value)}
                        placeholder="e.g. Rejected at first company, moving to next option" />
            </div>
            <Button onClick={submitMove} disabled={boardBusy} className="w-full rounded-lg" data-testid="move-confirm-btn">
              {boardBusy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Change company
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* M16: back out dialog */}
      <Dialog open={backOutOpen} onOpenChange={setBackOutOpen}>
        <DialogContent className="sm:max-w-md rounded-xl" data-testid="lineup-backout-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Back out of today's lineup?</DialogTitle>
            <DialogDescription>
              {app.member?.name || app.candidate?.full_name} will be marked Backed Out for {entry?.company_name} and the application will be <strong>closed</strong>. The candidate returns to the Candidate Directory — reassign from there to re-engage later.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Reason<span className="text-destructive ml-0.5">*</span></Label>
              <Textarea rows={2} data-testid="backout-reason-input" value={backOutReason}
                        onChange={(e) => setBackOutReason(e.target.value)}
                        placeholder="e.g. Candidate not reachable / not willing today" />
            </div>
            <Button onClick={submitBackOut} disabled={boardBusy} variant="destructive" className="w-full rounded-lg" data-testid="backout-confirm-btn">
              {boardBusy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Confirm back out
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* M17: Schedule Interview dialog */}
      <Dialog open={schedOpen} onOpenChange={setSchedOpen}>
        <DialogContent className="sm:max-w-md rounded-xl" data-testid="schedule-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Schedule Interview</DialogTitle>
            <DialogDescription>
              {app.member?.name || app.candidate?.full_name} moves to your Scheduled tab. On the interview
              day, confirm from there to mark them Going.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Interview date<span className="text-destructive ml-0.5">*</span></Label>
              <Input type="date" data-testid="schedule-date-input" value={schedForm.interview_date}
                     min={new Date().toISOString().slice(0, 10)}
                     onChange={(e) => setSchedForm({ ...schedForm, interview_date: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Company<span className="text-destructive ml-0.5">*</span></Label>
              <Select value={schedForm.company_id} onValueChange={(v) => setSchedForm({ ...schedForm, company_id: v })}>
                <SelectTrigger data-testid="schedule-company-select"><SelectValue placeholder="Choose company..." /></SelectTrigger>
                <SelectContent>
                  {boardCompanies.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}{!c.has_recipients ? " (no email recipient set)" : ""}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {boardCompanies.length === 0 && (
                <p className="text-xs text-amber-700">No companies allocated to you yet — ask your admin for allocation.</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label>Role / profile<span className="text-destructive ml-0.5">*</span></Label>
              <Input data-testid="schedule-role-input" value={schedForm.role}
                     onChange={(e) => setSchedForm({ ...schedForm, role: e.target.value })}
                     placeholder="e.g. Telecaller - Hindi" />
              <p className="text-[11px] text-muted-foreground">Pre-filled from the application — edit if this company needs a different role.</p>
            </div>
            <Button onClick={submitSchedule} disabled={busy} className="w-full rounded-lg" data-testid="schedule-submit-btn">
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Schedule Interview
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* M17: Not Interested dialog */}
      <Dialog open={niOpen} onOpenChange={setNiOpen}>
        <DialogContent className="sm:max-w-md rounded-xl" data-testid="not-interested-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Close as Not Interested?</DialogTitle>
            <DialogDescription>
              This closes the application and releases the candidate — they become available again in the
              Candidate Directory for any recruiter. Full history is kept.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Note (optional)</Label>
              <Textarea rows={2} data-testid="ni-note-input" value={niNote}
                        onChange={(e) => setNiNote(e.target.value)}
                        placeholder="e.g. Not willing to travel / salary expectation too high" />
            </div>
            <Button onClick={submitNotInterested} disabled={busy} variant="destructive" className="w-full rounded-lg" data-testid="ni-submit-btn">
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Confirm Not Interested
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* M17: Future prospect dialog */}
      <Dialog open={futOpen} onOpenChange={setFutOpen}>
        <DialogContent className="sm:max-w-md rounded-xl" data-testid="future-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Park as Future prospect?</DialogTitle>
            <DialogDescription>
              The candidate is tagged as a Future prospect in the Candidate Master and released — they stay
              searchable in the Candidate Directory for upcoming roles.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Note (optional)</Label>
              <Textarea rows={2} data-testid="future-note-input" value={futNote}
                        onChange={(e) => setFutNote(e.target.value)}
                        placeholder="e.g. Good profile — free only after notice period ends in March" />
            </div>
            <Button onClick={submitFuture} disabled={busy} className="w-full rounded-lg" data-testid="future-submit-btn">
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Tag as Future prospect
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* M17: Forward Lead dialog */}
      {/* Phase 36: single shared Forward Lead implementation */}
      <ForwardLeadDialog open={fwdOpen} onOpenChange={setFwdOpen}
        application={app ? { id: app.id, label: app.name } : null} onForwarded={load} />

      {/* M17: No Answer / Call Back dialog */}
      <Dialog open={cbOpen} onOpenChange={setCbOpen}>
        <DialogContent className="sm:max-w-md rounded-xl" data-testid="callback-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{isCallback ? "Reschedule call back" : "No answer — set a call back"}</DialogTitle>
            <DialogDescription>
              Pick when to try again. This frees your working slot so you can pick a new candidate, but this
              candidate stays with you (not reclaimable by others) and appears under your Call Backs tab.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Call back date</Label>
              <Input type="date" data-testid="callback-date-input" value={cbForm.followup_date}
                     min={new Date().toISOString().slice(0, 10)}
                     onChange={(e) => setCbForm((f) => ({ ...f, followup_date: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label>Note (optional)</Label>
              <Textarea rows={2} data-testid="callback-note-input" value={cbForm.note}
                        onChange={(e) => setCbForm((f) => ({ ...f, note: e.target.value }))}
                        placeholder="e.g. Rang twice, no answer — try after 6pm" />
            </div>
            <Button onClick={submitCallback} disabled={busy} className="w-full rounded-lg" data-testid="callback-submit-btn">
              {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} {isCallback ? "Update call back date" : "Set call back"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* M17: one-undisposed-candidate blocking dialog */}
      <Dialog open={!!conflict} onOpenChange={(o) => !o && setConflict(null)}>
        <DialogContent className="sm:max-w-md rounded-xl" data-testid="conflict-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Finish your current candidate first</DialogTitle>
            <DialogDescription data-testid="conflict-message">{conflict?.message}</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col sm:flex-row gap-2">
            <Button className="rounded-lg flex-1" data-testid="conflict-goto-btn"
                    onClick={() => { const id = conflict?.active_application_id; setConflict(null); navigate(`/recruiter/applications/${id}`); }}>
              Open {conflict?.candidate_name} ({conflict?.active_app_code})
            </Button>
            <Button variant="outline" className="rounded-lg bg-white" data-testid="conflict-close-btn" onClick={() => setConflict(null)}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
